import {
  ICreateWorkshiftParams,
  IPharmacyReportParams,
  IRateLevels,
  IUpdateTimingsParams,
  IReportIssue,
  IHistoryPharmacyApiParams,
  ILongContractRequest,
  ILongContractRequestModal,
  ILongContractRejectModel,
} from '../models/PharmacyModels';
import httpClient from './httpClient';
import { IBookedPharmacistDetails } from './types';
import {
  Agencies,
  ITEMS_PER_PAGE,
  PharmacyLegendVariant,
  TypeOfUser,
} from '../helpers/Constants';
import Routes from '../helpers/Routes';
import {
  ICancelBooking,
  ICancelMultipleBookingParams,
  ISyncRecordRequest,
} from '../models';
import {
  IDeleteContractParams,
  IGenericPagingApiParams,
  IGetPharmacyContractParams,
  IReplacementsParams,
} from '../models/Models';

class PharmacyController {
  basePath: string;
  constructor() {
    this.basePath = Routes.pharmacy.basePath;
  }

  pharmacyCalendar = (params: { startDate: string; endDate: string }) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.calendar}`, {
      criteria: params,
    });

  createWorkshift = (params: ICreateWorkshiftParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.workshifts}`, {
      multipleWorkshiftModel: params,
    });

  createWorkshiftContract = (params: ILongContractRequest) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacy.workshifts}${Routes.general.contract}`,
      {
        multipleWorkshiftModel: params,
      },
    );

  updateWorkshift = (params: ICreateWorkshiftParams) =>
    httpClient.put(`${this.basePath}${Routes.pharmacy.workshifts}`, {
      multipleWorkshiftModel: params,
    });

  updateWorkshiftContract = (params: ILongContractRequest) =>
    httpClient.put(
      `${this.basePath}${Routes.pharmacy.workshifts}${Routes.general.contract}`,
      {
        multipleWorkshiftModel: params,
      },
    );

  deleteWorkshift = (params: { id?: string; recurrenceId?: string }) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.workshifts}/delete`, {
      workshiftId: params.id,
      recurrenceId: params.recurrenceId || null,
    });

  workshift = (workshiftId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.pharmacy.workshifts}/${workshiftId}`,
    );

  reports = (params: IPharmacyReportParams) =>
    httpClient.post(`${this.basePath}${Routes.general.reports}`, {
      criteria: {
        ...params,
        itemPerPage: ITEMS_PER_PAGE,
      },
    });

  pharmacyReportFilter = () =>
    httpClient.get(`${this.basePath}${Routes.general.reports}`);

  replacements = (params: IReplacementsParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.replacements}`, {
      source: params?.source,
      pagingModel: {
        itemPerPage: ITEMS_PER_PAGE,
        ...params,
      },
    });

  addFavourite = (id: string) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.favorite}`, {
      pharmacistId: id,
    });

  removeFavourite = (id: string) =>
    httpClient.delete(`${this.basePath}${Routes.pharmacy.favorite}/${id}`);

  history = (params: IHistoryPharmacyApiParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.history}`, {
      ...params,
      source: params?.source,
      paging: {
        ...params.paging,
        itemPerPage: ITEMS_PER_PAGE,
      },
    });

  notes = (pharmacistId: string) =>
    httpClient.get(`${this.basePath}${Routes.pharmacy.notes}/${pharmacistId}`);

  createNote = (params: { pharmacistId: string; note: string }) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.notes}`, params);

  updateNote = (params: { pharmacistNotesId: string; note: string }) =>
    httpClient.put(`${this.basePath}${Routes.pharmacy.notes}`, params);

  deleteNote = (noteId: string) =>
    httpClient.delete(`${this.basePath}${Routes.pharmacy.notes}/${noteId}`);

  reportsPdf = (params: Omit<IPharmacyReportParams, 'page'>) =>
    httpClient.post(`${this.basePath}/${params.type}${Routes.general.getPDF}`, {
      criteria: params,
    });

  requestCancellationBooking = (params: ICancelBooking) =>
    httpClient.post(`${this.basePath}${Routes.general.cancelRequest}`, params);

  replacementsPdf = (source: Agencies) =>
    httpClient.get(
      `${this.basePath}${Routes.general.replacements}${Routes.general.getPDF}`,
      {
        params: {
          source,
        },
      },
    );

  bookedPharmacistDetails = ({ id, eventType }: IBookedPharmacistDetails) =>
    httpClient.get(`${this.basePath}${Routes.general.get}/${eventType}/${id}`);

  bookingRequestedList = (workshiftId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.pharmacy.requestedBookings}/${workshiftId}`,
    );

  approveRequest = (userType: TypeOfUser, bookingId: string) =>
    httpClient.post(
      `${userType.toLowerCase()}${Routes.general.bookings}${
        Routes.pharmacy.approve
      }`,
      {
        bookingId,
      },
    );

  ignoreRequest = (userType: TypeOfUser, bookingId: string) =>
    httpClient.post(
      `${userType.toLowerCase()}${Routes.general.bookings}${
        Routes.pharmacy.ignore
      }`,
      {
        bookingId,
      },
    );

  getHourlyRate = () =>
    httpClient.get(`${this.basePath}${Routes.pharmacy.workshiftData}`);

  ratingItems = (pharmacistId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.pharmacy.ratingItems}/${pharmacistId}`,
    );

  rate = (params: {
    bookingId: string;
    levels: Array<IRateLevels>;
    favorite: boolean;
    comment: string;
  }) => httpClient.post(`${this.basePath}${Routes.pharmacy.rate}`, params);

  rating = (pharmacistId: string) =>
    httpClient.get(`${this.basePath}${Routes.pharmacy.rating}/${pharmacistId}`);

  getBookedRating = (bookingId: string) =>
    httpClient.get(`${this.basePath}${Routes.pharmacy.rate}/${bookingId}`);

  updateTimings = (params: IUpdateTimingsParams) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.pharmacy.updateTime}`,
      params,
    );

  reportIssue = (params: IReportIssue) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.reportIssue}`, {
      bookingId: params.bookingId,
      issueType: params.issueType,
      description: params.description,
    });

  getReportIssue = () =>
    httpClient.get(`${this.basePath}${Routes.pharmacy.reportIssue}`);

  selfPharmacyCancelBooking = (bookingId: string) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.general.cancel}`,
      { bookingId },
    );

  doesPharmacyExist = (name: string) =>
    httpClient.post(
      `${Routes.authentication.authBasePath}${this.basePath}${Routes.general.exists}`,
      {
        name,
      },
    );

  addIncompatible = (pharmacistId: string) =>
    httpClient.post(`${this.basePath}${Routes.pharmacy.incompatibleMatches}`, {
      pharmacistId,
    });

  removeIncompatible = (pharmacistId: string) =>
    httpClient.delete(
      `${this.basePath}${Routes.pharmacy.incompatibleMatches}/${pharmacistId}`,
    );

  pharmacyPreApprove = (bookingId: string) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.pharmacy.preApprove}`,
      { bookingId },
    );

  pharmacistReplacementDetails = ({
    id: pharmacistId,
    isAutoSyncRecord,
  }: ISyncRecordRequest) =>
    httpClient.get(
      `${this.basePath}/${Routes.pharmacist.pharmacistBasePath}/${pharmacistId}`,
      {
        params: { isExternal: isAutoSyncRecord },
      },
    );

  getPharmacyEventsByDate = ({
    date,
    type,
  }: {
    date: string;
    type: PharmacyLegendVariant;
  }) =>
    httpClient.get(`${this.basePath}${Routes.general.events}/${type}/${date}`);

  getProfessionalPictures = (professionalIds: Array<string>) =>
    httpClient.post(`${this.basePath}${Routes.admin.professionalPictures}`, {
      professionalIds,
    });

  getOtherProfessionalsRequested = (workshiftId: string) =>
    httpClient.get(
      `${this.basePath}/${Routes.general.shifts}${Routes.professional.professionals}${Routes.admin.requested}/${workshiftId}`,
    );

  getRequestedLongContractDetails = (params: ILongContractRequestModal) =>
    httpClient.post(`${this.basePath}${Routes.general.requests}`, params);

  approveLongTermContract = (params: ILongContractRequestModal) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.admin.approve}`,
      params,
    );

  rejectLongTermContract = (params: ILongContractRequestModal) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.reject}`,
      params,
    );

  getLongTermBookingsById = (params: ILongContractRequestModal) =>
    httpClient.get(
      `${this.basePath}${Routes.general.bookings}/${params?.longTermContractId}/${params?.professionalId}`,
    );

  getPharmacyCompleteContract = async (params: IGetPharmacyContractParams) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.shifts}`,
      params,
    );

  cancelMultipleBookings = async (params: ICancelMultipleBookingParams) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.bookings}${Routes.general.cancel}`,
      params,
    );

  requestMultipleBookingsCancellation = (
    params: ICancelMultipleBookingParams,
  ) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.submit}${Routes.general.cancellation}`,
      params,
    );

  getProfessionalResume = (professionalId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.pharmacy.professionalResume}/${professionalId}`,
    );

  pharmacyDeleteContract = (params: IDeleteContractParams) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contracts}${Routes.general.delete}`,
      params,
    );
}

export default new PharmacyController();
